import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut, Sparkles, Loader2, Copy, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const kinds = [
  { value: 'blog', label: 'Blog post' },
  { value: 'service_copy', label: 'Service copy' },
  { value: 'case_study', label: 'Case study' },
  { value: 'social_post', label: 'Social post' }
];
const lengths = [
  { value: 'short', label: 'Short' },
  { value: 'medium', label: 'Medium' },
  { value: 'long', label: 'Long' }
];

const AdminContentStudioPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({
    kind: 'blog',
    topic: '',
    audience: '',
    tone: 'confident, expert, friendly',
    length: 'medium',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [copied, setCopied] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.topic.trim()) { toast({ title: 'Add a topic first' }); return; }
    setLoading(true);
    setContent('');
    try {
      const { data } = await api.post('/ai/content/generate', form);
      setContent(data.content || '');
    } catch (err) {
      toast({ title: 'Generation failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!content) return;
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-[#05080f]">
      <header className="border-b border-white/5 bg-[#05080f]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <div className="h-4 w-px bg-white/10"></div>
            <h1 className="text-white font-display text-lg inline-flex items-center gap-2">
              <Sparkles size={16} className="text-amber-300" /> Content Studio
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-white text-sm">{user?.username}</div>
              <div className="text-slate-500 text-xs">{user?.email}</div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10">
              <LogOut size={14} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-6">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">AI Content Studio</h2>
          <p className="text-slate-400 mt-1 text-sm">Draft blogs, service copy, case studies and social posts grounded in Pratham Innovations. Powered by GPT-5.6 Sol.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <form onSubmit={submit} className="card-dark rounded-3xl p-6 lg:col-span-2 space-y-4" data-testid="content-studio-form">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Content type</label>
              <div className="grid grid-cols-2 gap-2">
                {kinds.map((k) => (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => setForm({ ...form, kind: k.value })}
                    data-testid={`content-kind-${k.value}`}
                    className={`text-sm px-3 py-2 rounded-xl border transition-colors ${
                      form.kind === k.value
                        ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                        : 'bg-white/5 text-slate-300 border-white/10 hover:border-amber-500/30'
                    }`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Topic *</label>
              <Input
                data-testid="content-topic"
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                className="bg-[#0a1020] border-white/10 text-white h-11 rounded-xl"
                placeholder="e.g. Why multi-cloud DR beats vendor-native backup"
              />
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Audience</label>
              <Input
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                className="bg-[#0a1020] border-white/10 text-white h-11 rounded-xl"
                placeholder="CTOs, cloud leads, SREs…"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Tone</label>
                <Input
                  value={form.tone}
                  onChange={(e) => setForm({ ...form, tone: e.target.value })}
                  className="bg-[#0a1020] border-white/10 text-white h-11 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Length</label>
                <div className="flex gap-2">
                  {lengths.map((l) => (
                    <button
                      key={l.value}
                      type="button"
                      onClick={() => setForm({ ...form, length: l.value })}
                      className={`flex-1 text-xs py-2 rounded-lg border transition-colors ${
                        form.length === l.value
                          ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                          : 'bg-white/5 text-slate-300 border-white/10'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Notes</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-[#0a1020] border-white/10 text-white rounded-xl min-h-[100px]"
                placeholder="Key points to include, competitors to reference, must-avoid phrases…"
              />
            </div>

            <Button type="submit" disabled={loading} data-testid="content-generate-btn" className="btn-gradient rounded-full h-11 px-6 border-0 w-full">
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Drafting…</> : <><Sparkles size={16} className="mr-2" /> Generate</>}
            </Button>
          </form>

          <div className="card-dark rounded-3xl p-6 lg:col-span-3 min-h-[520px]" data-testid="content-output">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs tracking-widest text-amber-300">GENERATED DRAFT</div>
              {content && (
                <button onClick={copy} className="text-xs text-amber-200 hover:text-amber-100 inline-flex items-center gap-1.5">
                  {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy markdown</>}
                </button>
              )}
            </div>
            {loading && (
              <div className="flex items-center justify-center h-[380px] text-slate-400 text-sm">
                <Loader2 className="animate-spin mr-2" size={16} /> Drafting your piece…
              </div>
            )}
            {!loading && !content && (
              <div className="text-slate-500 text-sm">Fill the form and hit Generate. Your draft will appear here.</div>
            )}
            {content && (
              <pre className="whitespace-pre-wrap font-sans text-slate-200 text-sm leading-relaxed">{content}</pre>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminContentStudioPage;
